import React, { useState, useEffect, useRef } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { db } from '../../firebase/config';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { MATING_MOTIFS_CHAPTER } from '../../library/books/mating_motifs';

// Piece images for the creator palette
const PIECE_IMAGES = {
  'wR': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wR.svg', 'wN': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wN.svg',
  'wB': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wB.svg', 'wQ': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wQ.svg',
  'wK': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wK.svg', 'wP': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/wP.svg',
  'bR': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bR.svg', 'bN': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bN.svg',
  'bB': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bB.svg', 'bQ': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bQ.svg',
  'bK': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bK.svg', 'bP': 'https://raw.githubusercontent.com/ornicar/lila/master/public/piece/cburnett/bP.svg',
};

const baseRows = [8, 7, 6, 5, 4, 3, 2, 1];
const baseCols = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

export default function InteractiveBookViewer({ user, activeClass, isClassroomMode = false }) {
  const [books, setBooks] = useState([]);
  const [activeBookIndex, setActiveBookIndex] = useState(0);
  const [activeChapterIndex, setActiveChapterIndex] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const [mode, setMode] = useState('READ'); // 'READ' or 'EDIT'
  const [localGame, setLocalGame] = useState(new Chess());
  const [localFen, setLocalFen] = useState('');
  const [showSolution, setShowSolution] = useState(false);

  // Edit / Creation States
  const [showBookModal, setShowBookModal] = useState(false);
  const [bookForm, setBookForm] = useState({ title: '', description: '' });
  const [showChapterModal, setShowChapterModal] = useState(false);
  const [chapterForm, setChapterForm] = useState({ title: '' });
  const [showPageModal, setShowPageModal] = useState(false);
  const [pageForm, setPageForm] = useState({
    title: '',
    type: 'theory',
    fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    text: '',
    question: '',
    hint: '',
    answers: '',
    exampleTitle: '',
    exampleText: ''
  });

  // Creator Board Setup States
  const [setupBoard, setSetupBoard] = useState({});
  const [setupTurn, setSetupTurn] = useState('w');
  const [setupCastling, setSetupCastling] = useState({ K: true, Q: true, k: true, q: true });
  const [setupTool, setSetupTool] = useState('wK');

  // Bulk PGN States
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPgnData, setBulkPgnData] = useState('');

  const activeBook = books[activeBookIndex];
  const activeChapter = activeBook?.chapters?.[activeChapterIndex];
  const currentPage = activeChapter?.pages?.[currentPageIndex];

  // Role Checker
  const isOwnerOrAdmin = user && ['OWNER', 'ADMIN'].includes(user.role);

  // 1. Real-time fetch books from Firestore
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "interactive_books"), (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setBooks(list);

      // Seed default book if empty
      if (list.length === 0 && isOwnerOrAdmin) {
        seedDefaultBook();
      }
    });
    return () => unsub();
  }, [isOwnerOrAdmin]);

  // Seed Default Book
  const seedDefaultBook = async () => {
    try {
      await addDoc(collection(db, "interactive_books"), {
        title: "Artur Yusupov Fundamentals",
        description: "Tactics training course containing theory lessons and exercises.",
        owner: user?.email || 'admin',
        createdAt: Date.now(),
        chapters: [
          {
            id: `chapter_${Date.now()}`,
            title: "Chapter 1: Mating Motifs",
            pages: MATING_MOTIFS_CHAPTER.pages.map((p, idx) => ({
              id: `page_${Date.now()}_${idx}`,
              ...p
            }))
          }
        ]
      });
      toast.success("Seeded default mating motifs syllabus book! 📖");
    } catch (e) {
      console.error("Failed to seed default book:", e);
    }
  };

  // 2. Initialize local board when page changes
  useEffect(() => {
    if (currentPage) {
      resetLocalBoard();
    }
    setShowSolution(false);
  }, [currentPageIndex, currentPage]);

  const resetLocalBoard = () => {
    if (!currentPage) return;
    try {
      const g = new Chess();
      g.load(currentPage.fen);
      setLocalGame(g);
      setLocalFen(g.fen());
    } catch (e) {
      console.error("Error loading FEN:", e);
    }
  };

  const handleLocalMove = (sourceSquare, targetSquare) => {
    if (!currentPage) return false;
    try {
      const g = new Chess(localGame.fen());
      const move = g.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q'
      });
      if (move) {
        setLocalGame(g);
        setLocalFen(g.fen());
        
        if (currentPage.type === 'exercise') {
          if (currentPage.answers?.includes(move.san)) {
            toast.success("Correct move! 🎉 (Local Preview)");
          }
        }
        return true;
      }
    } catch (e) {}
    return false;
  };

  // Classroom Sync Controls
  const syncPositionToClass = async () => {
    if (!activeClass?.id || !currentPage) return toast.error("No active classroom session!");
    const loadingId = toast.loading("Syncing board position to class...");
    try {
      await updateDoc(doc(db, "classes", activeClass.id), {
        currentFen: currentPage.fen,
        classMode: 'PRACTICE',
        moveHistory: currentPage.moves || [],
        boardHighlights: {},
        boardArrows: [],
        boardItems: {},
        studentFens: {}
      });
      toast.success("Classroom Board Updated! 🚀", { id: loadingId });
    } catch (e) {
      toast.error("Failed to sync board position.", { id: loadingId });
    }
  };

  const syncNotesToClass = async () => {
    if (!activeClass?.id || !currentPage) return toast.error("No active classroom session!");
    const loadingId = toast.loading("Syncing explanation notes to class...");
    try {
      const notesText = currentPage.type === 'exercise' 
        ? `🧩 Quiz Active: ${currentPage.question}\n\nHint: ${currentPage.hint}`
        : `📚 Topic: ${currentPage.title}\n\n${currentPage.exampleText || currentPage.text}`;
      
      await updateDoc(doc(db, "classes", activeClass.id), {
        whiteboardText: notesText
      });
      toast.success("Classroom Notes Synced! 📝", { id: loadingId });
    } catch (e) {
      toast.error("Failed to sync notes.", { id: loadingId });
    }
  };

  const launchQuizToClass = async () => {
    if (!activeClass?.id || !currentPage) return toast.error("No active classroom session!");
    const loadingId = toast.loading("Launching quiz to students...");
    try {
      await updateDoc(doc(db, "classes", activeClass.id), {
        classMode: 'QUIZ',
        studentFens: {},
        quizData: {
          question: currentPage.question,
          answers: currentPage.answers || [],
          endTime: Date.now() + (180 * 1000),
          points: 15,
          active: true,
          solvedBy: []
        }
      });
      toast.success("Quiz is now live for all students! 🎯", { id: loadingId });
    } catch (e) {
      toast.error("Failed to launch quiz.", { id: loadingId });
    }
  };

  const showAnswerToClass = async () => {
    if (!activeClass?.id || !currentPage) return toast.error("No active classroom session!");
    const loadingId = toast.loading("Revealing solution...");
    try {
      const tempG = new Chess(currentPage.fen);
      (currentPage.answers || []).forEach(m => {
        try { tempG.move(m); } catch (err) {}
      });

      await updateDoc(doc(db, "classes", activeClass.id), {
        currentFen: tempG.fen(),
        classMode: 'PRACTICE',
        moveHistory: currentPage.answers || [],
        whiteboardText: `✅ Exercise Solution:\n${(currentPage.answers || []).join(" ➔ ")}`
      });
      toast.success("Solution shown on classroom board! 💡", { id: loadingId });
    } catch (e) {
      toast.error("Failed to show solution.", { id: loadingId });
    }
  };

  // Creator Actions: Create Book
  const handleCreateBook = async (e) => {
    e.preventDefault();
    if (!bookForm.title.trim()) return toast.error("Book title is required!");
    const loading = toast.loading("Saving Book...");
    try {
      await addDoc(collection(db, "interactive_books"), {
        title: bookForm.title.trim(),
        description: bookForm.description.trim(),
        owner: user?.email || 'admin',
        createdAt: Date.now(),
        chapters: []
      });
      setBookForm({ title: '', description: '' });
      setShowBookModal(false);
      toast.success("Book created! 📖", { id: loading });
    } catch (err) {
      toast.error("Failed to save book.", { id: loading });
    }
  };

  // Creator Actions: Delete Book
  const handleDeleteBook = async (bookId) => {
    if (!window.confirm("Are you sure you want to delete this book permanently?")) return;
    try {
      await deleteDoc(doc(db, "interactive_books", bookId));
      setActiveBookIndex(0);
      setActiveChapterIndex(0);
      setCurrentPageIndex(0);
      toast.success("Book deleted.");
    } catch (e) {
      toast.error("Failed to delete book.");
    }
  };

  // Creator Actions: Create Chapter
  const handleCreateChapter = async (e) => {
    e.preventDefault();
    if (!activeBook) return toast.error("Select a book first!");
    if (!chapterForm.title.trim()) return toast.error("Chapter title is required!");
    const loading = toast.loading("Adding Chapter...");
    try {
      const updatedChapters = [...(activeBook.chapters || []), {
        id: `chapter_${Date.now()}`,
        title: chapterForm.title.trim(),
        pages: []
      }];
      await updateDoc(doc(db, "interactive_books", activeBook.id), {
        chapters: updatedChapters
      });
      setChapterForm({ title: '' });
      setShowChapterModal(false);
      toast.success("Chapter added!", { id: loading });
    } catch (err) {
      toast.error("Failed to add chapter.", { id: loading });
    }
  };

  // Creator Actions: Delete Chapter
  const handleDeleteChapter = async (chapIndex) => {
    if (!window.confirm("Delete this chapter? All pages inside will be lost.")) return;
    try {
      const updatedChapters = activeBook.chapters.filter((_, i) => i !== chapIndex);
      await updateDoc(doc(db, "interactive_books", activeBook.id), {
        chapters: updatedChapters
      });
      setActiveChapterIndex(0);
      setCurrentPageIndex(0);
      toast.success("Chapter deleted.");
    } catch (e) {
      toast.error("Failed to delete chapter.");
    }
  };

  // PGN parsing helpers
  const parsePGNText = (text) => {
    const gameRegex = /((?:\[[A-Za-z]+\s+".*?"\]\s*\r?\n?)+)([^]*?)(?=(?:\[[A-Za-z]+\s+".*?"\])|$)/g;
    const games = [];
    let match;
    while ((match = gameRegex.exec(text)) !== null) {
      const tagText = match[1];
      const moveText = match[2].trim();
      const tags = {};
      const tagRegex = /\[([A-Za-z]+)\s+"(.*?)"\]/g;
      let tagMatch;
      while ((tagMatch = tagRegex.exec(tagText)) !== null) {
        tags[tagMatch[1]] = tagMatch[2];
      }
      if (Object.keys(tags).length > 0 || moveText.length > 0) {
        games.push({ tags, moveText });
      }
    }
    return games;
  };

  const cleanMoves = (moveText) => {
    let clean = moveText.replace(/\{[^}]*\}/g, '');
    clean = clean.replace(/;.*$/gm, '');
    let prevLength;
    do {
      prevLength = clean.length;
      clean = clean.replace(/\([^()]*\)/g, '');
    } while (clean.length !== prevLength);
    clean = clean.replace(/\d+\s*\.{1,3}/g, '');
    clean = clean.replace(/\$\d+/g, '');
    clean = clean.replace(/(?:1-0|0-1|1\/2-1\/2|\*)/g, '');
    clean = clean.trim().replace(/\s+/g, ' ');
    return clean;
  };

  // Bulk PGN Importer inside dynamic chapter creator
  const handleBulkPgnImport = async (e) => {
    e.preventDefault();
    if (!activeChapter) return toast.error("Select a chapter first!");
    if (!bulkPgnData.trim()) return toast.error("Please paste PGN data first!");

    const loading = toast.loading("Importing PGN contents...");
    try {
      const parsedGames = parsePGNText(bulkPgnData);
      if (parsedGames.length === 0) {
        toast.error("No valid PGN studies found.", { id: loading });
        return;
      }

      const newPages = parsedGames.map((g, idx) => {
        const isPuzzle = g.tags.SetUp === "1" || g.tags.SetUp === 1 || g.tags.FEN;
        const fen = g.tags.FEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        const movesList = cleanMoves(g.moveText).split(' ').filter(Boolean);
        
        let title = g.tags.Event || (g.tags.White ? `${g.tags.White} vs ${g.tags.Black}` : `Page #${idx + 1}`);
        if (title === "vs" || !title) title = `Page #${idx + 1}`;

        if (isPuzzle) {
          return {
            id: `page_${Date.now()}_${idx}`,
            type: "exercise",
            title: title,
            fen: fen,
            question: g.tags.Event ? `Find the winning continuation from ${g.tags.Event}.` : "Find the best continuation.",
            hint: "Look for forced checks and sacrifices.",
            answers: movesList
          };
        } else {
          return {
            id: `page_${Date.now()}_${idx}`,
            type: "theory",
            title: title,
            fen: fen,
            text: `Game study: ${title}. Analyze the moves and positional plans.`,
            exampleTitle: title,
            exampleText: g.moveText,
            moves: movesList
          };
        }
      });

      const updatedChapters = [...activeBook.chapters];
      updatedChapters[activeChapterIndex] = {
        ...activeChapter,
        pages: [...(activeChapter.pages || []), ...newPages]
      };

      await updateDoc(doc(db, "interactive_books", activeBook.id), {
        chapters: updatedChapters
      });

      setShowBulkModal(false);
      setBulkPgnData('');
      toast.success(`Successfully imported ${newPages.length} pages into ${activeChapter.title}!`, { id: loading });
    } catch (err) {
      console.error(err);
      toast.error("PGN Import failed.", { id: loading });
    }
  };

  // Creator Board Setup helpers
  const handleBoardClick = (sq) => {
    const nb = { ...setupBoard };
    if (setupTool === 'eraser') {
      delete nb[sq];
    } else {
      nb[sq] = setupTool;
    }
    setSetupBoard(nb);
    updateFenFromSetup(nb, setupTurn, setupCastling);
  };

  const updateFenFromSetup = (board, turn, castleInfo) => {
    let fen = '';
    for (let r = 8; r >= 1; r--) {
      let empty = 0;
      for (let c = 0; c < 8; c++) {
        const sq = `${String.fromCharCode(97 + c)}${r}`;
        const p = board[sq];
        if (p) {
          if (empty > 0) { fen += empty; empty = 0; }
          fen += p[0] === 'w' ? p[1].toUpperCase() : p[1].toLowerCase();
        } else {
          empty++;
        }
      }
      if (empty > 0) fen += empty;
      if (r > 1) fen += '/';
    }
    let castlingStr = '';
    if (castleInfo.K) castlingStr += 'K';
    if (castleInfo.Q) castlingStr += 'Q';
    if (castleInfo.k) castlingStr += 'k';
    if (castleInfo.q) castlingStr += 'q';
    if (!castlingStr) castlingStr = '-';

    setPageForm(prev => ({ ...prev, fen: `${fen} ${turn} ${castlingStr} - 0 1` }));
  };

  const clearSetupBoard = () => {
    setSetupBoard({});
    setPageForm(prev => ({ ...prev, fen: `8/8/8/8/8/8/8/8 ${setupTurn} - - 0 1` }));
    toast.success("Board Cleared! 🧹");
  };

  const handleCreatePage = async (e) => {
    e.preventDefault();
    if (!activeChapter) return toast.error("Select a chapter first!");
    if (!pageForm.title.trim()) return toast.error("Page title is required!");

    const loading = toast.loading("Saving Page...");
    try {
      const answersArray = pageForm.answers.split(',').map(s => s.trim()).filter(Boolean);
      const newPage = {
        id: `page_${Date.now()}`,
        type: pageForm.type,
        title: pageForm.title.trim(),
        fen: pageForm.fen.trim(),
        text: pageForm.text.trim(),
        question: pageForm.question.trim(),
        hint: pageForm.hint.trim(),
        answers: answersArray,
        exampleTitle: pageForm.exampleTitle.trim(),
        exampleText: pageForm.exampleText.trim()
      };

      const updatedChapters = [...activeBook.chapters];
      updatedChapters[activeChapterIndex] = {
        ...activeChapter,
        pages: [...(activeChapter.pages || []), newPage]
      };

      await updateDoc(doc(db, "interactive_books", activeBook.id), {
        chapters: updatedChapters
      });

      setShowPageModal(false);
      setPageForm({
        title: '',
        type: 'theory',
        fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
        text: '',
        question: '',
        hint: '',
        answers: '',
        exampleTitle: '',
        exampleText: ''
      });
      setSetupBoard({});
      toast.success("Page added successfully!", { id: loading });
    } catch (err) {
      toast.error("Failed to add page.", { id: loading });
    }
  };

  const handleDeletePage = async (pageIndex) => {
    if (!window.confirm("Delete this page?")) return;
    try {
      const updatedPages = activeChapter.pages.filter((_, i) => i !== pageIndex);
      const updatedChapters = [...activeBook.chapters];
      updatedChapters[activeChapterIndex] = {
        ...activeChapter,
        pages: updatedPages
      };
      await updateDoc(doc(db, "interactive_books", activeBook.id), {
        chapters: updatedChapters
      });
      setCurrentPageIndex(0);
      toast.success("Page deleted.");
    } catch (e) {
      toast.error("Failed to delete page.");
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      gap: '12px',
      color: 'var(--text-primary)',
      background: 'var(--bg-primary)',
      boxSizing: 'border-box'
    }}>
      {/* ── MODE SELECTION TABS & CONTROLS ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select 
            value={activeBookIndex} 
            onChange={(e) => {
              setActiveBookIndex(Number(e.target.value));
              setActiveChapterIndex(0);
              setCurrentPageIndex(0);
            }}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              border: '1px solid rgba(163, 177, 198, 0.4)',
              fontWeight: 'bold'
            }}
          >
            {books.map((b, idx) => (
              <option key={b.id} value={idx}>📖 {b.title}</option>
            ))}
          </select>

          {activeBook && (
            <select
              value={activeChapterIndex}
              onChange={(e) => {
                setActiveChapterIndex(Number(e.target.value));
                setCurrentPageIndex(0);
              }}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                border: '1px solid rgba(163, 177, 198, 0.4)',
                fontWeight: 'bold'
              }}
            >
              {(activeBook.chapters || []).map((ch, idx) => (
                <option key={ch.id} value={idx}>📁 {ch.title}</option>
              ))}
            </select>
          )}
        </div>

        {isOwnerOrAdmin && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setMode(mode === 'READ' ? 'EDIT' : 'READ')}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 'bold',
                cursor: 'pointer',
                background: mode === 'EDIT' ? '#f59e0b' : '#3b82f6',
                color: mode === 'EDIT' ? 'black' : 'white'
              }}
            >
              {mode === 'EDIT' ? "👁️ Read Mode" : "🛠️ Book Creator Mode"}
            </button>
          </div>
        )}
      </div>

      {/* ── CREATOR MODE DASHBOARD ── */}
      {mode === 'EDIT' && isOwnerOrAdmin ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(163, 177, 198, 0.2)',
          borderRadius: '12px',
          padding: '20px',
          flex: 1,
          overflowY: 'auto'
        }}>
          <h2 style={{ margin: 0, color: '#3b82f6', fontSize: '18px', fontWeight: 'bold' }}>
            🛠️ Interactive Books Builder Dashboard
          </h2>

          {/* Book / Chapter Creation Panel */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={() => setShowBookModal(true)} style={{ padding: '8px 15px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
              📚 New Book
            </button>
            {activeBook && (
              <>
                <button onClick={() => setShowChapterModal(true)} style={{ padding: '8px 15px', background: '#10b981', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                  📁 Add Chapter
                </button>
                <button onClick={() => handleDeleteBook(activeBook.id)} style={{ padding: '8px 15px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                  🗑️ Delete Active Book
                </button>
              </>
            )}
          </div>

          {/* Active Book Detail */}
          {activeBook && (
            <div style={{ borderTop: '1px solid rgba(163, 177, 198, 0.2)', paddingTop: '15px' }}>
              <h3 style={{ margin: '0 0 5px 0' }}>Book: {activeBook.title}</h3>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '13px' }}>{activeBook.description}</p>
              
              {/* Chapters List */}
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-secondary)' }}>Chapters in this Book:</h4>
                {(activeBook.chapters || []).map((ch, chIdx) => (
                  <div key={ch.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(255, 255, 255, 0.02)',
                    padding: '12px 18px',
                    borderRadius: '8px',
                    border: '1px solid rgba(163, 177, 198, 0.2)',
                    marginBottom: '8px'
                  }}>
                    <div>
                      <strong style={{ display: 'block' }}>{ch.title}</strong>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {(ch.pages || []).length} pages
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => {
                          setActiveChapterIndex(chIdx);
                          setShowPageModal(true);
                        }}
                        style={{ padding: '5px 10px', background: '#f59e0b', color: 'black', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}
                      >
                        ➕ Add Page
                      </button>
                      <button
                        onClick={() => {
                          setActiveChapterIndex(chIdx);
                          setShowBulkModal(true);
                        }}
                        style={{ padding: '5px 10px', background: '#10b981', color: 'black', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}
                      >
                        📥 Bulk PGN Import
                      </button>
                      <button
                        onClick={() => handleDeleteChapter(chIdx)}
                        style={{ padding: '5px 10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── READER MODE ── */
        currentPage ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            gap: '12px'
          }}>
            {/* Page Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 14px',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              border: '1px solid rgba(163, 177, 198, 0.2)'
            }}>
              <div>
                <span style={{
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {activeChapter?.title}
                </span>
                <h3 style={{ margin: '2px 0 0 0', fontSize: '16px', fontWeight: 'bold' }}>
                  {currentPage.title}
                </h3>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {isOwnerOrAdmin && mode === 'EDIT' && (
                  <button
                    onClick={() => handleDeletePage(currentPageIndex)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      background: '#ef4444',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '12px',
                      marginRight: '10px'
                    }}
                  >
                    🗑️ Delete Page
                  </button>
                )}
                <button
                  onClick={() => setCurrentPageIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentPageIndex === 0}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    fontWeight: 'bold',
                    cursor: currentPageIndex === 0 ? 'not-allowed' : 'pointer',
                    background: currentPageIndex === 0 ? 'rgba(163, 177, 198, 0.2)' : '#3b82f6',
                    color: 'white',
                    fontSize: '12px'
                  }}
                >
                  ◀ Prev
                </button>
                <span style={{ fontSize: '12px', alignSelf: 'center', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                  {currentPageIndex + 1} / {(activeChapter?.pages || []).length}
                </span>
                <button
                  onClick={() => setCurrentPageIndex(prev => Math.min((activeChapter?.pages || []).length - 1, prev + 1))}
                  disabled={currentPageIndex === (activeChapter?.pages || []).length - 1}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    fontWeight: 'bold',
                    cursor: currentPageIndex === (activeChapter?.pages || []).length - 1 ? 'not-allowed' : 'pointer',
                    background: currentPageIndex === (activeChapter?.pages || []).length - 1 ? 'rgba(163, 177, 198, 0.2)' : '#3b82f6',
                    color: 'white',
                    fontSize: '12px'
                  }}
                >
                  Next ▶
                </button>
              </div>
            </div>

            {/* Viewer Layout */}
            <div style={{
              display: 'flex',
              flexDirection: isClassroomMode ? 'column' : 'row',
              gap: '12px',
              flex: 1,
              minHeight: 0
            }}>
              {/* Board Area */}
              <div style={{
                width: isClassroomMode ? '100%' : '45%',
                maxWidth: isClassroomMode ? '220px' : '400px',
                alignSelf: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '2px solid rgba(163, 177, 198, 0.3)',
                  aspectRatio: '1/1'
                }}>
                  <Chessboard
                    position={localFen}
                    onPieceDrop={handleLocalMove}
                    boardWidth={isClassroomMode ? 220 : 380}
                    customDarkSquareStyle={{ backgroundColor: '#8ca2ad' }}
                    customLightSquareStyle={{ backgroundColor: '#dee3e6' }}
                  />
                </div>
                <button
                  onClick={resetLocalBoard}
                  style={{
                    padding: '5px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    background: 'rgba(163, 177, 198, 0.3)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🔄 Reset Board Preview
                </button>
              </div>

              {/* Text Area */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(163, 177, 198, 0.2)',
                borderRadius: '8px',
                padding: '12px',
                overflowY: 'auto'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{
                    background: currentPage.type === 'exercise' ? '#ef4444' : '#10b981',
                    color: 'black',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    width: 'fit-content',
                    textTransform: 'uppercase'
                  }}>
                    {currentPage.type}
                  </span>

                  {currentPage.type === 'theory' ? (
                    <>
                      <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                        {currentPage.text}
                      </p>
                      {currentPage.exampleTitle && (
                        <div style={{
                          padding: '10px',
                          background: 'rgba(59, 130, 246, 0.08)',
                          borderRadius: '6px',
                          borderLeft: '3px solid #3b82f6',
                          marginTop: '8px'
                        }}>
                          <strong style={{ display: 'block', fontSize: '12px', color: '#60a5fa', marginBottom: '4px' }}>
                            🎮 Example: {currentPage.exampleTitle}
                          </strong>
                          <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>
                            {currentPage.exampleText}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 'bold' }}>
                        🎯 {currentPage.question}
                      </p>
                      {currentPage.hint && (
                        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                          💡 Hint: {currentPage.hint}
                        </p>
                      )}

                      {/* Local solution display */}
                      <div style={{ marginTop: '10px' }}>
                        <button
                          onClick={() => setShowSolution(!showSolution)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            background: showSolution ? '#ef4444' : '#f59e0b',
                            color: 'black',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          {showSolution ? "Hide Answer" : "Reveal Answer (Local)"}
                        </button>
                        {showSolution && currentPage.answers && (
                          <div style={{
                            marginTop: '6px',
                            padding: '8px',
                            background: 'rgba(245, 158, 11, 0.1)',
                            borderRadius: '4px',
                            border: '1px dashed #f59e0b',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: '#f59e0b'
                          }}>
                            Solution Moves: {currentPage.answers.join(" ➔ ")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Classroom Controls */}
                {isClassroomMode && (
                  <div style={{
                    marginTop: '14px',
                    paddingTop: '10px',
                    borderTop: '1px solid rgba(163, 177, 198, 0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      Coach Actions (Syncs Live Class)
                    </span>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                      <button
                        onClick={syncPositionToClass}
                        style={{
                          padding: '8px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          background: '#10b981',
                          color: 'black',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        🚀 Load to Board
                      </button>
                      <button
                        onClick={syncNotesToClass}
                        style={{
                          padding: '8px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        📝 Sync Notes
                      </button>
                    </div>
                    {currentPage.type === 'exercise' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                        <button
                          onClick={launchQuizToClass}
                          style={{
                            padding: '8px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          🎯 Launch Quiz
                        </button>
                        <button
                          onClick={showAnswerToClass}
                          style={{
                            padding: '8px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            background: '#f59e0b',
                            color: 'black',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          💡 Show Answer
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: '#64748b', textAlign: 'center', marginTop: '40px' }}>
            No pages found in this chapter. Go to Creator Mode to add pages or bulk upload a PGN.
          </div>
        )
      )}

      {/* ── DIALOGS / MODALS ── */}

      {/* Book Creation Modal */}
      {showBookModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <form onSubmit={handleCreateBook} style={{ background: 'var(--bg-primary)', padding: '25px', borderRadius: '12px', width: '400px', display: 'flex', flexDirection: 'column', gap: '15px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
            <h3 style={{ margin: 0, display: 'flex', justifyContent: 'space-between' }}>
              📚 Create New Book <span onClick={() => setShowBookModal(false)} style={{ cursor: 'pointer', color: '#ef4444' }}>✖</span>
            </h3>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Book Title</label>
              <input type="text" value={bookForm.title} onChange={e => setBookForm({ ...bookForm, title: e.target.value })} required style={{ width: '100%', padding: '10px', marginTop: '5px', borderRadius: '6px', background: 'var(--bg-primary)', color: 'white', border: '1px solid rgba(163, 177, 198, 0.4)' }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Description</label>
              <textarea value={bookForm.description} onChange={e => setBookForm({ ...bookForm, description: e.target.value })} style={{ width: '100%', padding: '10px', marginTop: '5px', borderRadius: '6px', background: 'var(--bg-primary)', color: 'white', border: '1px solid rgba(163, 177, 198, 0.4)', height: '80px', resize: 'none' }} />
            </div>
            <button type="submit" style={{ padding: '12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
              Create Book
            </button>
          </form>
        </div>
      )}

      {/* Chapter Creation Modal */}
      {showChapterModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <form onSubmit={handleCreateChapter} style={{ background: 'var(--bg-primary)', padding: '25px', borderRadius: '12px', width: '400px', display: 'flex', flexDirection: 'column', gap: '15px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
            <h3 style={{ margin: 0, display: 'flex', justifyContent: 'space-between' }}>
              📁 Add New Chapter <span onClick={() => setShowChapterModal(false)} style={{ cursor: 'pointer', color: '#ef4444' }}>✖</span>
            </h3>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Chapter Title</label>
              <input type="text" value={chapterForm.title} onChange={e => setChapterForm({ title: e.target.value })} required placeholder="e.g. Chapter 2: Defensive Tactics" style={{ width: '100%', padding: '10px', marginTop: '5px', borderRadius: '6px', background: 'var(--bg-primary)', color: 'white', border: '1px solid rgba(163, 177, 198, 0.4)' }} />
            </div>
            <button type="submit" style={{ padding: '12px', background: '#10b981', color: 'black', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
              Add Chapter
            </button>
          </form>
        </div>
      )}

      {/* Bulk PGN Modal */}
      {showBulkModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <form onSubmit={handleBulkPgnImport} style={{ background: 'var(--bg-primary)', padding: '25px', borderRadius: '12px', width: '550px', display: 'flex', flexDirection: 'column', gap: '15px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
            <h3 style={{ margin: 0, display: 'flex', justifyContent: 'space-between' }}>
              📥 Bulk PGN Import (Auto setup pages) <span onClick={() => setShowBulkModal(false)} style={{ cursor: 'pointer', color: '#ef4444' }}>✖</span>
            </h3>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              Paste PGN data here. Games with FEN tags become Exercises; others become Theory pages.
            </span>
            <textarea
              value={bulkPgnData}
              onChange={e => setBulkPgnData(e.target.value)}
              required
              placeholder='[Event "Anastasia Mate"]&#10;[SetUp "1"]&#10;[FEN "r4rk1/pp3ppp/8/3N3Q/8/3R4/PPP2PPP/5RK1 w - - 0 1"]&#10;&#10;1. Ne7+ Kh8 2. Qxh7+ Kxh7 3. Rh3# *'
              style={{ width: '100%', height: '220px', padding: '12px', borderRadius: '6px', background: 'var(--bg-primary)', color: '#10b981', border: '1px solid rgba(163, 177, 198, 0.4)', fontFamily: 'monospace', resize: 'none' }}
            />
            <button type="submit" style={{ padding: '12px', background: '#10b981', color: 'black', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
              Import PGN & Setup Chapter Pages
            </button>
          </form>
        </div>
      )}

      {/* Page Creator Modal (Setup Board) */}
      {showPageModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '10px' }}>
          <div style={{ background: '#e0e5ec', width: '900px', maxWidth: '95%', padding: '25px', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.45)', display: 'flex', flexDirection: 'column', gap: '15px', maxHeight: '95vh', overflowY: 'auto', boxShadow: 'var(--panel-shadow)' }}>
            <h3 style={{ margin: 0, color: 'black', display: 'flex', justifyContent: 'space-between', fontWeight: '800' }}>
              ➕ Create Page (Board Setup) <span onClick={() => setShowPageModal(false)} style={{ cursor: 'pointer', color: '#ef4444' }}>✖</span>
            </h3>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
              {/* Left Column: Board Setup */}
              <div style={{ flex: 1, minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', background: '#e0e5ec', padding: '8px', borderRadius: '12px', boxShadow: 'var(--neu-shadow-inset)', border: '1px solid rgba(255, 255, 255, 0.4)' }}>
                  {['wK','wQ','wR','wB','wN','wP','bK','bQ','bR','bB','bN','bP'].map(p => (
                    <img
                      key={p}
                      src={PIECE_IMAGES[p]}
                      alt={p}
                      onClick={() => setSetupTool(p)}
                      style={{
                        width: '26px',
                        height: '26px',
                        cursor: 'pointer',
                        background: setupTool === p ? '#3b82f6' : 'transparent',
                        borderRadius: '4px',
                        boxShadow: setupTool === p ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                        border: '1px solid rgba(255, 255, 255, 0.4)'
                      }}
                    />
                  ))}
                  <div
                    onClick={() => setSetupTool('eraser')}
                    style={{
                      width: '26px',
                      height: '26px',
                      cursor: 'pointer',
                      background: setupTool === 'eraser' ? '#ef4444' : 'transparent',
                      color: setupTool === 'eraser' ? 'white' : 'black',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxShadow: setupTool === 'eraser' ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                      border: '1px solid rgba(255, 255, 255, 0.4)'
                    }}
                  >
                    🗑️
                  </div>
                  <button
                    type="button"
                    onClick={clearSetupBoard}
                    style={{
                      padding: '2px 8px',
                      height: '26px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      boxShadow: 'var(--neu-shadow)',
                      marginLeft: '5px'
                    }}
                  >
                    Clear Board 🧹
                  </button>
                </div>

                {/* Setup Chessboard Grid */}
                <div style={{
                  width: '100%',
                  maxWidth: '320px',
                  margin: '0 auto',
                  aspectRatio: '1/1',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(8, 1fr)',
                  gridTemplateRows: 'repeat(8, 1fr)',
                  border: '6px solid var(--bg-primary)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: 'var(--neu-shadow-inset)'
                }}>
                  {baseRows.map((row, rIdx) => baseCols.map((col, cIdx) => {
                    const sq = `${col}${row}`;
                    const isDark = (rIdx + cIdx) % 2 === 1;
                    return (
                      <div
                        key={sq}
                        onClick={() => handleBoardClick(sq)}
                        style={{
                          background: isDark ? '#769656' : '#eeeed2',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        {setupBoard[sq] && (
                          <img src={PIECE_IMAGES[setupBoard[sq]]} alt="p" style={{ width: '90%' }} />
                        )}
                      </div>
                    );
                  }))}
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => { setSetupTurn('w'); updateFenFromSetup(setupBoard, 'w', setupCastling); }}
                    style={{ padding: '6px 12px', background: setupTurn === 'w' ? '#3b82f6' : 'white', color: setupTurn === 'w' ? 'white' : 'black', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    ⚪ White Turn
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSetupTurn('b'); updateFenFromSetup(setupBoard, 'b', setupCastling); }}
                    style={{ padding: '6px 12px', background: setupTurn === 'b' ? '#3b82f6' : 'white', color: setupTurn === 'b' ? 'white' : 'black', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    ⚫ Black Turn
                  </button>
                </div>
              </div>

              {/* Right Column: Page Form */}
              <form onSubmit={handleCreatePage} style={{ flex: 1, minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <label style={{ color: 'black', fontWeight: 'bold' }}>
                    <input type="radio" checked={pageForm.type === 'theory'} onChange={() => setPageForm({ ...pageForm, type: 'theory' })} /> 📖 Theory Page
                  </label>
                  <label style={{ color: 'black', fontWeight: 'bold' }}>
                    <input type="radio" checked={pageForm.type === 'exercise'} onChange={() => setPageForm({ ...pageForm, type: 'exercise' })} /> 🎯 Exercise Page
                  </label>
                </div>

                <div>
                  <label style={{ color: 'black', fontWeight: 'bold', fontSize: '12px' }}>Page Title</label>
                  <input type="text" value={pageForm.title} onChange={e => setPageForm({ ...pageForm, title: e.target.value })} required placeholder="e.g. Anastasia's Mate Introduction" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
                </div>

                <div>
                  <label style={{ color: 'black', fontWeight: 'bold', fontSize: '12px' }}>FEN Position</label>
                  <input type="text" value={pageForm.fen} onChange={e => { setPageForm({ ...pageForm, fen: e.target.value }); }} required style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box', fontFamily: 'monospace', color: '#10b981' }} />
                </div>

                {pageForm.type === 'theory' ? (
                  <>
                    <div>
                      <label style={{ color: 'black', fontWeight: 'bold', fontSize: '12px' }}>Explanation Text</label>
                      <textarea value={pageForm.text} onChange={e => setPageForm({ ...pageForm, text: e.target.value })} placeholder="Describe the mating theme..." style={{ width: '100%', height: '70px', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box', resize: 'none' }} />
                    </div>
                    <div>
                      <label style={{ color: 'black', fontWeight: 'bold', fontSize: '12px' }}>Example Title (Optional)</label>
                      <input type="text" value={pageForm.exampleTitle} onChange={e => setPageForm({ ...pageForm, exampleTitle: e.target.value })} placeholder="e.g. J.Gast - E.Bhend 1987" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ color: 'black', fontWeight: 'bold', fontSize: '12px' }}>Example Move Notation (Optional)</label>
                      <textarea value={pageForm.exampleText} onChange={e => setPageForm({ ...pageForm, exampleText: e.target.value })} placeholder="e.g. 1...Ne2+ 2.Kh1 Qxh2+ 3.Kxh2 Rh4#" style={{ width: '100%', height: '50px', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box', resize: 'none' }} />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label style={{ color: 'black', fontWeight: 'bold', fontSize: '12px' }}>Puzzle Question</label>
                      <input type="text" value={pageForm.question} onChange={e => setPageForm({ ...pageForm, question: e.target.value })} placeholder="e.g. Deliver Anastasia's Mate in 3 moves." style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ color: 'black', fontWeight: 'bold', fontSize: '12px' }}>Hint</label>
                      <input type="text" value={pageForm.hint} onChange={e => setPageForm({ ...pageForm, hint: e.target.value })} placeholder="e.g. Look for a queen sacrifice." style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ color: '#10b981', fontWeight: 'bold', fontSize: '12px' }}>Solution Moves (Comma separated)</label>
                      <input type="text" value={pageForm.answers} onChange={e => setPageForm({ ...pageForm, answers: e.target.value })} required placeholder="e.g. Ne7+, Qxh7+, Rh3#" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #10b981', boxSizing: 'border-box' }} />
                    </div>
                  </>
                )}

                <button type="submit" style={{ padding: '12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', marginTop: '10px' }}>
                  Save Page to Chapter
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
